import { useMemo, useCallback } from 'react';
import CrmLocationResolver from 'crm-cards/CrmLocationResolver';
import useRecordSubject from '../crm_ui/profile/hooks/useRecordSubject';
import { Section } from 'foundations-components/transitional/components/Section';
import AuthContainer from '../containers/AuthContainer';
import { useRouteMatch } from 'react-router-dom';
import { CRM_LOCATION_NAMES } from 'crm-cards-sdk/constants/CrmLocationName';
import { findTimelineContainer } from 'crm-cards/content-locators/findTimelineContainer';
import { getCardRegistry } from '../cardRegistry/RecordPageCardRegistry';
import RecordCustomizationSettingsLink from 'crm-links/layoutEditor/RecordCustomizationSettingsLink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isScoped } from '../containers/ScopeOperators';
import ScopesContainer from '../containers/ScopesContainer';
import { getIsInAcceptanceTestEnvironment } from './utils/acceptanceTestHelpers';
import { RecordsFeatures } from '../constants/crmFeatures';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { Metrics, getObjectTypeIdForMetrics } from '../utils/Metrics';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { COMPANY_TYPE_ID, denormalizeTypeId, normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { NavMarker } from 'react-rhumb';
import DefaultTabContainer from '../containers/DefaultTabContainer';
import { View } from 'foundations-components/components/View';
import { useTheme } from 'foundations-theming/utils';
import FullHeightScrollContainer from '../crm_ui/profile/common/components/FullHeightScrollContainer';
import { Flex } from 'foundations-components/components/Flex';
import { getI18nProvider } from 'quartz-core/framework/i18n';
import { CATCHUP_CARDS, CATCHUP_CARDS_M2, SCROLLABLE_TABS } from '../constants/gates';
import { usageTracker } from '../usageTracker';
import { insertCatchUpTabPrototype, insertCatchUpTabPrototypeM2 } from './utils/insertCatchUpTabPrototype';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const scrollContainerProps = {
  'data-three-column-scrollable-container': true,
  'data-scroll-trackable-container': 'middle-column'
};
const getLocationTransformer = ({
  gates,
  objectTypeId
}) => location => {
  if (!location || location.crmLocationName !== CRM_LOCATION_NAMES.CRM_RECORD_MIDDLE || location.container.layout.type !== 'TABS') {
    return location;
  }
  const newContainer = Object.assign({}, location.container, {
    layout: Object.assign({}, location.container.layout, {
      showSettingsButton: true,
      use: 'scrollable'
    })
  });
  const newLocation = Object.assign({}, location, {
    container: newContainer
  });
  const isUngatedForDataQuality = gates.includes(CATCHUP_CARDS_M2);
  if (normalizeTypeId(objectTypeId) === COMPANY_TYPE_ID && gates.includes(CATCHUP_CARDS)) {
    return isUngatedForDataQuality ? insertCatchUpTabPrototypeM2(newLocation) : insertCatchUpTabPrototype(newLocation);
  }
  return newLocation;
};
export default function CardLocationMiddlepane() {
  const {
    objectTypeId,
    subjectId
  } = useRecordSubject();
  const additionalLocationData = useMemo(() => {
    return {
      objectTypeId,
      objectId: +subjectId
    };
  }, [objectTypeId, subjectId]);
  const auth = AuthContainer.get();
  const gates = auth.gates;
  const scopes = auth.user.scopes;
  const match = useRouteMatch(['/:objectType/:subjectId/view/:viewId', '/record/:objectType/:subjectId/view/:viewId']);
  const isInAcceptanceTestEnvironment = getIsInAcceptanceTestEnvironment();
  const hasTimelineViewParam = match ? match.params.viewId === '1' : false;
  const transformLocation = useMemo(() => getLocationTransformer({
    gates,
    objectTypeId
  }), [gates, objectTypeId]);
  useOnCrmMessageTopic('CONTAINER_TAB_OPENED', ({
    isOpenedAsDefault,
    isActivitiesTab,
    tabLabel
  }) => {
    if (isOpenedAsDefault) {
      DefaultTabContainer.set({
        isActivitiesTab
      });
    }
    Metrics.counter('recordTabOpened', {
      isOpenedAsDefault: String(isOpenedAsDefault),
      isActivitiesTab: String(isActivitiesTab),
      type: isActivitiesTab ? 'Activities' : tabLabel === 'Overview' ? 'Overview' : 'Custom',
      objectTypeId: getObjectTypeIdForMetrics(objectTypeId)
    }).increment();
    if (!isOpenedAsDefault) {
      // This will allow us to get rough numbers on overview tab usage, however we will not
      // be able to distinguish between the default overview tab and an admin made one
      CrmLogger.log('recordInteractions', {
        action: 'middle pane tab opened',
        objectType: denormalizeTypeId(objectTypeId),
        type: isActivitiesTab ? 'Activities' : tabLabel === 'Overview' ? 'Overview' : 'Custom'
      });
    }
  });
  const handleTrackTabOpen = useCallback(({
    tabLabel,
    isActivitiesTab,
    isOpenedAsDefault
  }) => {
    usageTracker.track('middlePaneTabOpenedInteraction', {
      tabLabel,
      isActivitiesTab,
      isOpenedAsDefault
    });
  }, []);
  const handleTrackCustomizeRecordLink = useCallback(({
    fromObjectTypeId
  }) => CrmLogger.log('recordInteractions', {
    action: 'customize record link clicked',
    fromObjectTypeId
  }), []);
  useOnCrmMessageTopic(TOPIC_NAMES.CONTAINER_TAB_OPENED, handleTrackTabOpen);
  const defaultToTimeline = isInAcceptanceTestEnvironment || hasTimelineViewParam;
  const getContainerForInitialFocus = useCallback(locationData => {
    if (defaultToTimeline) {
      return findTimelineContainer(locationData);
    }
    return undefined;
  }, [defaultToTimeline]);
  const errorMarker = useMemo(() => /*#__PURE__*/_jsx(NavMarker, {
    name: "CARD_LOCATION_MIDDLEPANE_ERROR"
  }), []);
  const theme = useTheme();
  const isUngatedForScrollInsideTabs = useMemo(() => {
    return gates.includes(SCROLLABLE_TABS);
  }, [gates]);
  return /*#__PURE__*/_jsx(Section, {
    height: "100%",
    "data-selenium-test": "cardLocationMiddlepane",
    "data-test-id": "middle-pane",
    children: isUngatedForScrollInsideTabs ? /*#__PURE__*/_jsx(View, {
      marginInline: "300",
      marginBlockStart: "250",
      blockSize: `calc(100% - ${theme.space['250']})`,
      children: /*#__PURE__*/_jsx(CrmLocationResolver, {
        additionalLocationData: additionalLocationData,
        enableVirtualization: true,
        features: RecordsFeatures,
        findInitialFocus: getContainerForInitialFocus,
        gates: gates,
        i18nLoader: getI18nProvider(),
        location: CRM_LOCATION_NAMES.CRM_RECORD_MIDDLE,
        registry: getCardRegistry(),
        scopes: scopes,
        errorNavMarker: errorMarker,
        _experimentalTransformCrmLocation: transformLocation
      })
    }) : /*#__PURE__*/_jsx(FullHeightScrollContainer, {
      scrollContainerProps: scrollContainerProps,
      children: /*#__PURE__*/_jsxs(View, {
        marginInline: "300",
        marginBlockStart: "250",
        paddingBlockEnd: "1300",
        children: [isScoped(ScopesContainer.get(), ['crm-page-editor-admin']) ? /*#__PURE__*/_jsx(Flex, {
          justifyContent: "end",
          paddingInline: "400",
          children: /*#__PURE__*/_jsx(RecordCustomizationSettingsLink, {
            objectTypeId: objectTypeId,
            onTrackClick: handleTrackCustomizeRecordLink,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "crm-records-ui.tabs.recordEditorLink"
            })
          })
        }) : null, /*#__PURE__*/_jsx(CrmLocationResolver, {
          usageTracker: usageTracker,
          additionalLocationData: additionalLocationData,
          enableVirtualization: true,
          features: RecordsFeatures,
          findInitialFocus: getContainerForInitialFocus,
          gates: gates,
          i18nLoader: getI18nProvider(),
          location: CRM_LOCATION_NAMES.CRM_RECORD_MIDDLE,
          registry: getCardRegistry(),
          scopes: scopes,
          errorNavMarker: errorMarker
        })]
      })
    })
  });
}